import { useState } from "react";
import { t } from "i18next";
import { type FileData, updateFile } from "@/services/files";
import { getVisitTitle } from "@/lib/utils";

interface UseVisitActionsState {
  changeTitleVisitByFile: (file: FileData, title: string) => Promise<void>;
  deleteVisitByFile: (file: FileData) => Promise<void>;
  result: string | null;
  loading: boolean;
  error: string | null;
}

export const useVisitActions = (): UseVisitActionsState => {
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const deleteVisitByFile = async (file: FileData): Promise<void> => {
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      await updateFile({ fileId: file.id, fileStatus: 0 });
      setResult(
        t("visits.actions.messages.deletedSuccess", {
          visitTitle: getVisitTitle(file.title, file.rowno),
        })
      );
    } catch {
      setError(t("visits.actions.messages.deletedError"));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const changeTitleVisitByFile = async (
    file: FileData,
    title: string
  ): Promise<void> => {
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      await updateFile({ fileId: file.id, title: title });
      setResult(
        t("visits.actions.messages.changeTitleSuccess", {
          visitTitle: getVisitTitle(file.title, file.rowno),
          newTitle: title,
        })
      );
    } catch {
      setError(t("visits.actions.messages.changeTitleError"));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return { deleteVisitByFile, changeTitleVisitByFile, result, loading, error };
};
